// Note fonts. The interface itself always uses Nunito (headings Archivo,
// code JetBrains Mono); see index.css. Notes can use any font below; those
// are loaded from Google Fonts only when a note needs one.

export const DEFAULT_NOTE_FONT = "nunito";

// Per-note typeface options (note editor)
export const NOTE_FONT_OPTIONS = [
  { id: "nunito", label: "Nunito" },
  { id: "mono", label: "JetBrains Mono" },
  { id: "roboto", label: "Roboto" },
  { id: "opensans", label: "Open Sans" },
  { id: "lato", label: "Lato" },
  { id: "sourcesans", label: "Source Sans Pro" },
  { id: "paper", label: "Merriweather" },
  { id: "handwritten", label: "Patrick Hand" },
  { id: "caveat", label: "Caveat" },
  { id: "lora", label: "Lora" },
  { id: "poppins", label: "Poppins" },
  { id: "playfair", label: "Playfair Display" },
  { id: "ebgaramond", label: "EB Garamond" },
  { id: "cormorant", label: "Cormorant Garamond" },
  { id: "crimson", label: "Crimson Text" },
  { id: "space", label: "Space Grotesk" },
  { id: "sourcecodepro", label: "Source Code Pro" },
  { id: "firacode", label: "Fira Code" },
  { id: "inconsolata", label: "Inconsolata" },
  { id: "orbitron", label: "Orbitron" },
  { id: "amatic", label: "Amatic SC" },
  { id: "indieflower", label: "Indie Flower" },
  { id: "greatvibes", label: "Great Vibes" },
  { id: "lucida", label: "Lucida Grande" },
];

export const FONT_MAP = {
  mono: '"JetBrains Mono", Menlo, Monaco, Consolas, "Courier New", monospace',
  roboto:
    '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  opensans:
    '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  lato: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  sourcesans:
    '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  nunito: '"Nunito", "Varela Round", ui-rounded, sans-serif',
  paper: '"Merriweather", Georgia, "Times New Roman", serif',
  handwritten:
    '"Patrick Hand", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  caveat: '"Caveat", "Comic Sans MS", cursive',
  lora: '"Lora", Georgia, "Times New Roman", serif',
  poppins:
    '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  playfair: '"Playfair Display", Georgia, "Times New Roman", serif',
  ebgaramond: '"EB Garamond", Georgia, "Times New Roman", serif',
  cormorant: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  crimson: '"Crimson Text", Georgia, "Times New Roman", serif',
  space:
    "'Space Grotesk', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
  sourcecodepro:
    '"Source Code Pro", Menlo, Monaco, Consolas, "Courier New", monospace',
  firacode: '"Fira Code", Menlo, Monaco, Consolas, "Courier New", monospace',
  inconsolata:
    '"Inconsolata", Menlo, Monaco, Consolas, "Courier New", monospace',
  orbitron: "Orbitron, 'Segoe UI', Roboto, Arial, sans-serif",
  amatic: "'Amatic SC', 'Comic Sans MS', cursive",
  indieflower: "'Indie Flower', 'Comic Sans MS', cursive",
  greatvibes: "'Great Vibes', cursive",
  lucida:
    '"Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Arial, sans-serif',
};

// Google Fonts family query for each note font that is not loaded up front
const GOOGLE_FAMILY = {
  roboto: "Roboto:wght@400;700",
  opensans: "Open+Sans:wght@400;700",
  lato: "Lato:wght@400;700",
  sourcesans: "Source+Sans+Pro:wght@400;700",
  paper: "Merriweather:ital,wght@0,400;0,700;1,400",
  handwritten: "Patrick+Hand",
  caveat: "Caveat:wght@400;700",
  lora: "Lora:wght@400;700",
  poppins: "Poppins:wght@400;700",
  playfair: "Playfair+Display:wght@400;700",
  ebgaramond: "EB+Garamond:wght@400;700",
  cormorant: "Cormorant+Garamond:wght@400;700",
  crimson: "Crimson+Text:wght@400;700",
  space: "Space+Grotesk:wght@400;700",
  sourcecodepro: "Source+Code+Pro:wght@400;700",
  firacode: "Fira+Code:wght@400;700",
  inconsolata: "Inconsolata:wght@400;700",
  orbitron: "Orbitron:wght@400;700",
  amatic: "Amatic+SC:wght@400;700",
  indieflower: "Indie+Flower",
  greatvibes: "Great+Vibes",
};

const requestedFonts = new Set();

// Load one or more note fonts on demand
export const ensureFontsLoaded = (fontIds) => {
  const families = [].concat(fontIds)
    .filter((id) => GOOGLE_FAMILY[id] && !requestedFonts.has(id));
  if (!families.length || typeof document === "undefined") return;
  families.forEach((id) => requestedFonts.add(id));
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${families
    .map((id) => `family=${GOOGLE_FAMILY[id]}`)
    .join("&")}&display=swap`;
  document.head.appendChild(link);
};

// Older notes saved "inter", which the app no longer uses
export const normalizeNoteFont = (fontId) =>
  !fontId || fontId === "inter" || !FONT_MAP[fontId] ? DEFAULT_NOTE_FONT : fontId;
